/**
 * The cached D1 graph (about 1.7M characters of JSON). On web it lives in its
 * own IndexedDB store, so it never counts against the ~5 MB localStorage
 * quota that player data shares; elsewhere it stays in AsyncStorage.
 * Callers treat every failure as "no cache" and fall back to the bundle.
 */
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createStore, del, get, set, type UseStore } from "idb-keyval";
import { Platform } from "react-native";

import { CorruptRecord } from "./types";

/** The AsyncStorage (localStorage on web) key the graph was cached under. */
export const LEGACY_GRAPH_CACHE_KEY = "synapse_graph_cache";
export const WEB_GRAPH_DB = "synapse-graph";
const RECORD = "active";

export interface GraphCacheStore {
  /** The decoded record, undefined when absent, or a CorruptRecord. */
  read(): Promise<unknown>;
  write(value: unknown): Promise<void>;
  remove(): Promise<void>;
}

export function createWebGraphCache(
  makeStore: (db: string, store: string) => UseStore = createStore,
): GraphCacheStore {
  const store = makeStore(WEB_GRAPH_DB, "graph");
  // The graph used to be cached in localStorage. It is only a cache, so
  // dropping it is safe, and it frees most of the localStorage quota.
  let legacyDropped: Promise<void> | null = null;
  const dropLegacy = () =>
    (legacyDropped ??= AsyncStorage.removeItem(LEGACY_GRAPH_CACHE_KEY).catch(
      () => undefined,
    ));

  return {
    read: async () => {
      await dropLegacy();
      return get(RECORD, store);
    },
    write: async (value) => {
      await dropLegacy();
      await set(RECORD, value, store);
    },
    remove: () => del(RECORD, store),
  };
}

export function createNativeGraphCache(): GraphCacheStore {
  return {
    read: async () => {
      const raw = await AsyncStorage.getItem(LEGACY_GRAPH_CACHE_KEY);
      if (raw === null) return undefined;
      try {
        return JSON.parse(raw);
      } catch {
        return new CorruptRecord(raw);
      }
    },
    write: (value) =>
      AsyncStorage.setItem(LEGACY_GRAPH_CACHE_KEY, JSON.stringify(value)),
    remove: () => AsyncStorage.removeItem(LEGACY_GRAPH_CACHE_KEY),
  };
}

let graphCache: GraphCacheStore | null = null;

/** IndexedDB on web (when available), AsyncStorage everywhere else. */
export function getGraphCache(): GraphCacheStore {
  if (!graphCache) {
    graphCache =
      Platform.OS === "web" && typeof indexedDB !== "undefined"
        ? createWebGraphCache()
        : createNativeGraphCache();
  }
  return graphCache;
}

/** Tests: force a store (null restores platform selection). */
export function setGraphCache(next: GraphCacheStore | null): void {
  graphCache = next;
}
